import { useId, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import {
  KEY_DEFAULT_REMINDER_DAYS,
  KEYS_SYNC_STALE_HOURS,
  KEY_PROVIDERS,
  KEY_REMINDER_DAYS,
  keyProjectName,
  type AdminKey,
  type AdminKeys,
  type KeyFailure,
  type KeyProvider,
} from '@traceio-org/platform-contracts';
import {
  EmptyPageState,
  NavigationGlyph,
  OperationalDatePicker,
  PageHeading,
  PageTabs,
  RecordStatus,
} from '@traceio-org/ui';
import { AdminPage } from '../components/AdminPage';
import { AdminProblem } from '../components/AdminProblem';
import { LoadingBlock, SavedNotice, useNow } from '../components/feedback';
import { RadioGroup, TextField } from '../components/fields';
import { MenuSelect } from '../components/MenuSelect';
import { DateText, ScrollTable } from '../components/records';
import { WriteDialog, useDialogTarget } from '../components/WriteDialog';
import { describeWriteFailure } from '../api';
import { useAbilities, useAdmin } from '../context';
import { formatCount, plural } from '../format';
import { ENVIRONMENT_NAMES, formatAge } from '../health';
import { KeyGlyph, PROVIDER_LOGOS, ProviderTile } from '../keyProviders';
import {
  KEY_TABS,
  KIND_LABELS,
  PROJECT_FILTERS,
  QUICK_EXPIRIES,
  STATUS_LABELS,
  expiresCell,
  failureCovers,
  formatDay,
  hasRealExpiry,
  inProject,
  inTab,
  matchesSearch,
  MULTIPLE_PROJECTS,
  placeOf,
  projectParam,
  providerName,
  rotationSourceOf,
  rowActions,
  summaryMetrics,
  tabCounts,
  tabParam,
} from '../keys';
import { useKeysSummary } from '../keysSummary';
import { useAdminRead } from '../useAdminRead';

type Dialog =
  { kind: 'expiry'; key: AdminKey } | { kind: 'provider'; key: AdminKey } | { kind: 'rotate'; key: AdminKey };

/**
 * The expiry or rotation date of a key: quick choices from today (UTC), any day on Trace UI's calendar, or no expiry,
 * and the reminder in Trace UI's menu. The same fields for a provider that expires its keys and for a rotation date.
 */
function ExpiryFields({
  form,
  onChange,
  error,
  today,
  rotation,
}: {
  form: ExpiryForm;
  onChange: (update: (form: ExpiryForm) => ExpiryForm) => void;
  error: string | null;
  today: string;
  rotation: boolean;
}) {
  const dateId = useId();
  const quickId = useId();
  const set = (patch: Partial<ExpiryForm>) => onChange((current) => ({ ...current, ...patch }));
  return (
    <>
      <RadioGroup
        legend="This key"
        choices={[
          { value: 'date', label: rotation ? 'Rotate by' : 'Expires on' },
          { value: 'never', label: "Doesn't expire" },
        ]}
        value={form.mode}
        onChange={(value) => set({ mode: value as ExpiryForm['mode'] })}
      />
      {form.mode === 'date' ? (
        <>
          <div className="admin-field">
            <span className="admin-field__label" id={quickId}>
              {rotation ? 'Rotate in' : 'Expires in'}
            </span>
            <div className="admin-keys-quick" role="group" aria-labelledby={quickId}>
              {QUICK_EXPIRIES.map((choice) => {
                const day = choice.of(today);
                return (
                  <button
                    key={choice.label}
                    type="button"
                    className="admin-action"
                    aria-pressed={form.expiresOn === day}
                    title={formatDay(day)}
                    onClick={() => set({ expiresOn: day })}
                  >
                    {choice.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="admin-field" data-invalid={error ? 'true' : undefined}>
            {/* The picker's typed field is <id>-control; the hidden <id> carries the form value. */}
            <label className="admin-field__label" htmlFor={`${dateId}-control`}>
              {rotation ? 'Rotation date' : 'Expiry date'}
            </label>
            <OperationalDatePicker
              id={dateId}
              ariaLabel={rotation ? 'Rotation date' : 'Expiry date'}
              value={form.expiresOn}
              onChange={(value) => set({ expiresOn: value })}
              today={today}
              required
              {...(error ? { ariaDescribedBy: `${dateId}-error` } : {})}
            />
            <p className="admin-field__hint">
              {rotation
                ? 'The day you plan to rotate it by (UTC); this provider does not end its keys itself.'
                : 'The day the provider ends it (UTC).'}
            </p>
            {error ? (
              <p className="admin-field__error" id={`${dateId}-error`}>
                {error}
              </p>
            ) : null}
          </div>
          <MenuSelect
            label="Remind me"
            value={form.reminder}
            options={REMINDER_OPTIONS}
            onChange={(value) => set({ reminder: value })}
          />
        </>
      ) : null}
    </>
  );
}

const REMINDER_OPTIONS = [
  ...KEY_REMINDER_DAYS.map((days) => ({ value: String(days), label: `${days} days before` })),
  { value: 'none', label: 'No reminder' },
];

/** The summary strip (Trace UI's metrics strip, as on Health): expired and due in their colours, words beside. */
function KeysSummaryStrip({ keys }: { keys: AdminKeys }) {
  return (
    <section className="trace-page-metrics trace-page-metrics-strip" aria-label="Keys at a glance">
      {summaryMetrics(keys.counts).map((metric) => (
        <div key={metric.label} className="trace-page-metric">
          <span className="trace-page-metric-label">{metric.label}</span>
          <span className="trace-page-metric-value admin-keys-metric" data-tone={metric.tone ?? undefined}>
            {formatCount(metric.value)}
          </span>
        </div>
      ))}
    </section>
  );
}

function NotConnected() {
  return (
    <EmptyPageState
      icon={<KeyGlyph />}
      title="Keys hasn't synced from Infisical yet"
      description="A GitHub Actions job (Keys sync) lists the key names in Infisical, never a value, and sends them here every hour and after each deploy. Nothing has arrived for this environment yet."
    />
  );
}

/** "Synced 12 min ago" beside the environment: when the inventory shown was read from Infisical. */
function SyncedAt({ syncedAt, stale }: { syncedAt: string; stale: boolean }) {
  const now = useNow(30_000);
  return (
    <p className="admin-health-checked" data-state={stale ? 'partial' : 'ok'}>
      <span className="admin-health-checked__dot" aria-hidden="true" />
      <span>
        Synced{' '}
        <time dateTime={syncedAt} title={new Date(syncedAt).toUTCString()}>
          {formatAge(syncedAt, now)}
        </time>
      </span>
    </p>
  );
}

/** A quiet note when the latest sync is older than KEYS_SYNC_STALE_HOURS: the hourly job may be failing. */
function StaleNotice() {
  return (
    <p className="admin-keys-stale" role="note">
      The last sync from Infisical is more than {KEYS_SYNC_STALE_HOURS} hours old, so recent changes may be
      missing. The Keys sync workflow runs every hour; check its latest run in GitHub Actions.
    </p>
  );
}

/** A failure's place in words: the whole project ("Trace") or a folder of it (placeOf). */
function failurePlace(failure: KeyFailure): string {
  return failure.folder === '/' ? keyProjectName(failure.project) : placeOf(failure);
}

function PartialNotice({ keys }: { keys: AdminKeys }) {
  const headingId = useId();
  return (
    <section className="admin-health-partial" aria-labelledby={headingId}>
      <h2 id={headingId}>
        {keys.failures.length === 1
          ? `Couldn't read ${failurePlace(keys.failures[0]!)} at the last sync`
          : `Couldn't read ${keys.failures.length} projects or folders at the last sync`}
      </h2>
      <p>Their keys are shown as of the previous sync that read them, marked below; nothing is guessed.</p>
      <ul>
        {keys.failures.map((failure) => (
          <li key={`${failure.project}:${failure.folder}`}>
            {failurePlace(failure)}: {failure.message}
          </li>
        ))}
      </ul>
    </section>
  );
}

function KeysTable({
  keys,
  canManage,
  onAction,
  busy,
  failures,
  onTracking,
}: {
  keys: AdminKey[];
  canManage: boolean;
  onAction: (dialog: Dialog) => void;
  busy: boolean;
  /** Projects and folders the last sync could not read: their rows are as of an earlier sync. */
  failures: readonly KeyFailure[];
  /** Track this or Not a secret, at once (both are undone the same way). */
  onTracking: (key: AdminKey, tracked: boolean) => void;
}) {
  return (
    <ScrollTable caption="Keys" className="admin-table admin-keys-table" busy={busy}>
      <thead>
        <tr>
          <th scope="col">Key</th>
          <th scope="col">Kind</th>
          <th scope="col">Used by</th>
          <th scope="col">Last changed</th>
          <th scope="col">Expires</th>
          <th scope="col">Status</th>
          <th scope="col">
            <span className="admin-sr-only">Actions</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {keys.map((key) => {
          const expires = expiresCell(key);
          const status = STATUS_LABELS[key.status];
          const actions = rowActions(key, canManage);
          const stale = failures.some((failure) => failureCovers(failure, key));
          return (
            <tr key={`${key.project}:${key.folder}:${key.name}`} data-stale={stale || undefined}>
              <th scope="row">
                <span className="admin-key">
                  <ProviderTile provider={key.provider} providerName={key.providerName} />
                  <span className="admin-key__text">
                    <span className="admin-key__name">{key.name}</span>
                    <span className="admin-key__where admin-key__where--line">
                      {providerName(key)} · {placeOf(key)}
                    </span>
                    {/* A collapsed pair names its other half in full, on its own line (never cut or wrapped mid-name). */}
                    {key.pairedName ? (
                      <span className="admin-key__where admin-key__where--line">
                        Pairs with {key.pairedName}
                      </span>
                    ) : null}
                    {stale ? <span className="admin-key__stale">Not read at the last sync</span> : null}
                  </span>
                </span>
              </th>
              <td>{KIND_LABELS[key.kind]}</td>
              <td className="admin-muted">{key.usedBy}</td>
              <td className="admin-nowrap">
                <DateText iso={key.lastChangedAt} />
              </td>
              <td className="admin-nowrap">
                <span className="admin-keys-expires">{expires.primary}</span>
                {expires.secondary ? <span className="admin-key__where">{expires.secondary}</span> : null}
              </td>
              <td>
                <RecordStatus tone={status.tone}>{status.label}</RecordStatus>
              </td>
              <td className="admin-keys-actions">
                {actions.rotate ? (
                  <button
                    type="button"
                    className="admin-action"
                    onClick={() => onAction({ kind: 'rotate', key })}
                  >
                    How to rotate
                  </button>
                ) : null}
                {actions.provider ? (
                  <button
                    type="button"
                    className="admin-action"
                    onClick={() => onAction({ kind: 'provider', key })}
                    aria-label={`${actions.provider} for ${key.name}`}
                  >
                    {actions.provider}
                  </button>
                ) : null}
                {actions.expiry ? (
                  <button
                    type="button"
                    className="admin-action"
                    onClick={() => onAction({ kind: 'expiry', key })}
                    aria-label={`${actions.expiry === 'Edit' ? 'Edit the expiry of' : 'Set the expiry of'} ${key.name}`}
                  >
                    {actions.expiry}
                  </button>
                ) : null}
                {actions.tracking ? (
                  <button
                    type="button"
                    className="admin-action"
                    onClick={() => onTracking(key, actions.tracking === 'Track this')}
                    aria-label={`${actions.tracking}: ${key.name}, ${placeOf(key)}`}
                  >
                    {actions.tracking}
                  </button>
                ) : null}
              </td>
            </tr>
          );
        })}
      </tbody>
    </ScrollTable>
  );
}

/** How to rotate a key, step by step, with the provider's page and Infisical. Read only: it changes nothing. */
function RotateSteps({ keyRow, keys }: { keyRow: AdminKey; keys: AdminKeys }) {
  const source = rotationSourceOf(keyRow);
  const logo = keyRow.provider ? PROVIDER_LOGOS[keyRow.provider] : null;
  const environment = ENVIRONMENT_NAMES[keys.environment];
  const names = keyRow.pairedName ? `${keyRow.pairedName} and ${keyRow.name}` : keyRow.name;
  const openInfisical = keys.consoleUrl ? (
    <>
      {' '}
      (
      <a href={keys.consoleUrl} target="_blank" rel="noopener noreferrer">
        open Infisical
      </a>
      )
    </>
  ) : null;
  const replace = (
    <>
      Replace <code>{names}</code> in Infisical, project {keyProjectName(keyRow.project)}, folder{' '}
      <code>{keyRow.folder}</code>, {environment} environment
      {openInfisical}.
    </>
  );
  const redeploy = <>Redeploy what uses it ({keyRow.usedBy}), so it reads the new value.</>;
  const record = <>Record the new expiry here.</>;
  let steps: ReactNode[];
  switch (source) {
    case 'infisical':
      // The same steps as docs/operations/secrets.md, Trace Admin Keys, Rotation.
      steps = [
        <>
          In Infisical
          {openInfisical}, open the machine identity behind <code>{keyRow.name}</code> and create a new client
          secret under Universal Auth. Never generate one yourself: Infisical only accepts the secrets it
          issued.
        </>,
        replace,
        redeploy,
        <>Once the new secret works, revoke the old client secret on the identity.</>,
        record,
      ];
      break;
    case 'trace':
      steps = [
        <>
          Generate a new random secret, for example with <code>openssl rand -base64 48</code>.
        </>,
        replace,
        redeploy,
        <>Anything signed with the old secret stops being accepted; people may have to sign in again.</>,
        record,
      ];
      break;
    case 'provider':
      steps = [
        logo!.consoleUrl ? (
          <>
            Make a new key at{' '}
            <a href={logo!.consoleUrl} target="_blank" rel="noopener noreferrer">
              {logo!.consoleName}
            </a>
            .
          </>
        ) : (
          <>Make a new key at {logo!.name}.</>
        ),
        replace,
        redeploy,
        <>Once the new key works, revoke the old one at {logo!.name}.</>,
        record,
      ];
      break;
    case 'unknown':
      steps = [
        keyRow.provider === 'other' ? (
          <>Make a new key at {providerName(keyRow)}.</>
        ) : (
          <>Make a new key with the provider that issued this one (Choose provider names it here).</>
        ),
        replace,
        redeploy,
        keyRow.provider === 'other' ? (
          <>Once the new key works, revoke the old one at {providerName(keyRow)}.</>
        ) : (
          <>Once the new key works, revoke the old one at that provider.</>
        ),
        record,
      ];
      break;
  }
  return (
    <ol className="admin-keys-steps">
      {steps.map((step, index) => (
        <li key={index}>{step}</li>
      ))}
    </ol>
  );
}

interface ExpiryForm {
  mode: 'date' | 'never';
  expiresOn: string;
  reminder: string;
}

function expiryFormOf(key: AdminKey): ExpiryForm {
  return {
    mode: key.expirySource === 'does_not_expire' ? 'never' : 'date',
    expiresOn: key.expiresOn ?? '',
    reminder:
      key.expirySource === 'set_by_you'
        ? key.reminderDays === null
          ? 'none'
          : String(key.reminderDays)
        : String(KEY_DEFAULT_REMINDER_DAYS),
  };
}

const VALID_DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Keys (/keys): the secret names of this Trace Admin's own environment in Infisical, with when each last changed and
 * what staff recorded about its expiry, most urgent first. Values are never read, shown or sent anywhere. Every staff
 * role reads it (each opening is audited); platform admins record expiries and providers (audited).
 */
export function KeysPage() {
  const { api } = useAdmin();
  const abilities = useAbilities();
  const { refresh: refreshSummary } = useKeysSummary();
  const read = useAdminRead('keys', (signal) => api.listKeys(signal));
  const keys = read.failure ? null : read.data;
  const [params, setParams] = useSearchParams();
  const tab = tabParam(params.get('tab'));
  const project = projectParam(params.get('project'));
  const [search, setSearch] = useState('');
  const searchId = useId();
  const [notice, setNotice] = useState<string | null>(null);
  const dialog = useDialogTarget<Dialog>();
  const [expiryForm, setExpiryForm] = useState<ExpiryForm>({ mode: 'date', expiresOn: '', reminder: '30' });
  const [expiryError, setExpiryError] = useState<string | null>(null);
  const [provider, setProvider] = useState<string>('');
  const [providerOther, setProviderOther] = useState('');
  const [providerError, setProviderError] = useState<string | null>(null);

  const inScope = useMemo(
    () => (keys ? keys.keys.filter((key) => inProject(key, project)) : []),
    [keys, project],
  );
  const counts = useMemo(() => tabCounts(inScope), [inScope]);
  const shown = useMemo(
    () => inScope.filter((key) => inTab(key, tab) && matchesSearch(key, search)),
    [inScope, tab, search],
  );

  const open = (target: Dialog) => {
    if (target.kind === 'expiry') {
      setExpiryForm(expiryFormOf(target.key));
      setExpiryError(null);
    }
    if (target.kind === 'provider') {
      setProvider(target.key.provider ?? '');
      setProviderOther(target.key.providerName ?? '');
      setProviderError(null);
    }
    dialog.show(target);
  };
  const chooseTab = (next: string) => {
    const nextParams = new URLSearchParams(params);
    if (next === 'attention') nextParams.delete('tab');
    else nextParams.set('tab', next);
    setParams(nextParams, { replace: true });
  };
  const chooseProject = (next: string) => {
    const nextParams = new URLSearchParams(params);
    if (next === 'all') nextParams.delete('project');
    else nextParams.set('project', next);
    setParams(nextParams, { replace: true });
  };
  const track = (key: AdminKey, tracked: boolean) => {
    api.setKeyTracking(key.project, key.name, { tracked }).then(
      () =>
        saved(
          tracked
            ? `${key.name} (${placeOf(key)}) is tracked.`
            : `${key.name} (${placeOf(key)}) is hidden: not a secret. Track this from the Hidden tab undoes it.`,
        ),
      (error: unknown) => setNotice(describeWriteFailure(error).message),
    );
  };
  const saved = (message: string) => {
    setNotice(message);
    read.retry();
    refreshSummary();
  };

  let body: ReactNode;
  if (read.failure) {
    body = <AdminProblem failure={read.failure} subject="the keys" onRetry={read.retry} />;
  } else if (!keys) {
    body = <LoadingBlock label="Reading Infisical" />;
  } else if (!keys.connected) {
    body = <NotConnected />;
  } else {
    const activeTab = KEY_TABS.find((t) => t.id === tab)!;
    body = (
      <div className="admin-stack admin-keys">
        {keys.source === 'fake' ? (
          <div className="admin-dev-note" role="note">
            <p>
              Synthetic data: this environment reads Trace&apos;s local stand-in for Infisical (Trace
              dev&apos;s key names, made-up dates), not Infisical itself.
            </p>
          </div>
        ) : null}
        {keys.stale ? <StaleNotice /> : null}
        <KeysSummaryStrip keys={keys} />
        {keys.failures.length > 0 ? <PartialNotice keys={keys} /> : null}
        <div className="admin-keys-toolbar">
          <PageTabs
            label="Filter keys"
            tabs={KEY_TABS.map((t) => ({ id: t.id, label: t.label, count: counts[t.id] }))}
            activeId={tab}
            onChange={chooseTab}
          />
          {MULTIPLE_PROJECTS ? (
            <MenuSelect
              label="Project"
              showLabel={false}
              value={project}
              options={PROJECT_FILTERS}
              onChange={chooseProject}
            />
          ) : null}
          <form role="search" className="admin-search" onSubmit={(event) => event.preventDefault()}>
            <label htmlFor={searchId} className="admin-sr-only">
              Search keys
            </label>
            <span className="admin-search__icon" aria-hidden="true">
              <NavigationGlyph name="Search" />
            </span>
            <input
              id={searchId}
              type="search"
              value={search}
              onChange={(event) => setSearch(event.currentTarget.value)}
              placeholder="Search keys"
              autoComplete="off"
              spellCheck={false}
              maxLength={100}
            />
          </form>
        </div>
        {/* Quietly, so nothing leaves the page unnoticed: how many entries the rules (or staff) keep out of view. */}
        {tab !== 'hidden' && counts.hidden > 0 ? (
          <p className="admin-keys-hidden-note">
            {plural(counts.hidden, 'entry', 'entries')} hidden as configuration or Not a secret.{' '}
            <button type="button" className="admin-link-button" onClick={() => chooseTab('hidden')}>
              Show hidden
            </button>
          </p>
        ) : null}
        <p className="admin-results-status admin-sr-only" role="status" aria-live="polite">
          {`${activeTab.label}: ${plural(shown.length, 'key', 'keys')}`}
        </p>
        {shown.length > 0 ? (
          <KeysTable
            keys={shown}
            canManage={abilities.manageKeys}
            onAction={open}
            busy={read.loading}
            failures={keys.failures}
            onTracking={track}
          />
        ) : (
          <p className="admin-keys-empty">
            {search.trim()
              ? `No key matches "${search.trim()}" here.`
              : tab === 'attention'
                ? 'Nothing needs attention: no key is expired or due, and every provider token has an expiry.'
                : tab === 'hidden'
                  ? 'Nothing is hidden: every entry here is tracked as a secret.'
                  : 'No key of this kind here.'}
          </p>
        )}
      </div>
    );
  }

  const target = dialog.target;
  return (
    <AdminPage title="Keys">
      <PageHeading
        title="Keys"
        actions={
          <div className="admin-health-actions">
            {keys ? (
              <p className="admin-health-environment">
                <span className="admin-sr-only">Environment: </span>
                {ENVIRONMENT_NAMES[keys.environment]}
              </p>
            ) : null}
            {keys?.syncedAt ? <SyncedAt syncedAt={keys.syncedAt} stale={keys.stale} /> : null}
            {keys?.consoleUrl ? (
              <a className="admin-action" href={keys.consoleUrl} target="_blank" rel="noopener noreferrer">
                Open Infisical
              </a>
            ) : null}
          </div>
        }
      />
      <SavedNotice message={notice} />
      {body}
      <WriteDialog
        key={dialog.key}
        open={dialog.open && target?.kind === 'expiry'}
        onClose={dialog.close}
        // A provider that ends its keys itself (DigitalOcean) has an expiry; for the others the day is the rotation
        // date staff choose. Same control, same recording.
        title={target?.kind === 'expiry' && !hasRealExpiry(target.key) ? 'Set rotation date' : 'Set expiry'}
        {...(target ? { description: `${target.key.name} · ${placeOf(target.key)}` } : {})}
        submitLabel="Save"
        onSubmit={async () => {
          if (target?.kind !== 'expiry') return 'invalid';
          if (expiryForm.mode === 'date') {
            if (!VALID_DAY.test(expiryForm.expiresOn)) {
              setExpiryError(
                hasRealExpiry(target.key)
                  ? 'Choose the day this key expires.'
                  : 'Choose the day to rotate this key by.',
              );
              return 'invalid';
            }
            setExpiryError(null);
            await api.setKeyExpiry(target.key.project, target.key.name, {
              expiry: 'date',
              expiresOn: expiryForm.expiresOn,
              reminderDays: expiryForm.reminder === 'none' ? null : Number(expiryForm.reminder),
            });
          } else {
            await api.setKeyExpiry(target.key.project, target.key.name, { expiry: 'never' });
          }
          return undefined;
        }}
        onSaved={() =>
          target?.kind === 'expiry'
            ? saved(
                expiryForm.mode === 'date'
                  ? `${target.key.name}: ${hasRealExpiry(target.key) ? 'expires' : 'rotate by'} ${formatDay(expiryForm.expiresOn)}.`
                  : `${target.key.name}: doesn't expire.`,
              )
            : undefined
        }
      >
        {target?.kind === 'expiry' && keys ? (
          <ExpiryFields
            form={expiryForm}
            onChange={setExpiryForm}
            error={expiryError}
            today={keys.today}
            rotation={!hasRealExpiry(target.key)}
          />
        ) : null}
      </WriteDialog>
      <WriteDialog
        key={`provider-${dialog.key}`}
        open={dialog.open && target?.kind === 'provider'}
        onClose={dialog.close}
        title="Choose provider"
        {...(target ? { description: `${target.key.name} · ${placeOf(target.key)}` } : {})}
        submitLabel="Save"
        onSubmit={async () => {
          if (target?.kind !== 'provider') return 'invalid';
          if (!provider) {
            setProviderError('Choose who issued this key.');
            return 'invalid';
          }
          const otherName = providerOther.trim();
          if (provider === 'other' && (otherName.length === 0 || otherName.length > 60)) {
            setProviderError('Name the provider (up to 60 characters).');
            return 'invalid';
          }
          setProviderError(null);
          await api.setKeyProvider(
            target.key.project,
            target.key.name,
            provider === 'rules'
              ? { provider: null }
              : provider === 'other'
                ? { provider: 'other', name: otherName }
                : { provider: provider as KeyProvider },
          );
          return undefined;
        }}
        onSaved={() =>
          target?.kind === 'provider' ? saved(`${target.key.name}: provider saved.`) : undefined
        }
      >
        <RadioGroup
          legend="Who issued it"
          hint="Saved for this key name in this project and environment; the logo and the kind follow it."
          choices={[
            ...KEY_PROVIDERS.filter((value) => value !== 'other').map((value) => ({
              value,
              label: PROVIDER_LOGOS[value].name,
            })),
            { value: 'other', label: 'Other…', description: 'A provider not listed here, in your words.' },
            ...(target?.kind === 'provider' && target.key.providerChosen
              ? [{ value: 'rules', label: "Use the name's rules", description: 'Forget the choice.' }]
              : []),
          ]}
          value={provider}
          onChange={setProvider}
          error={provider === 'other' ? null : providerError}
        />
        {provider === 'other' ? (
          <TextField
            label="Provider name"
            value={providerOther}
            onChange={setProviderOther}
            maxLength={60}
            error={providerError}
            required
          />
        ) : null}
      </WriteDialog>
      <WriteDialog
        key={`rotate-${dialog.key}`}
        open={dialog.open && target?.kind === 'rotate'}
        onClose={dialog.close}
        title="How to rotate"
        {...(target ? { description: `${target.key.name} · ${placeOf(target.key)}` } : {})}
        submitLabel="Close"
        submitHidden
        cancelLabel="Close"
        onSubmit={() => Promise.resolve(undefined)}
        onSaved={() => undefined}
      >
        {target?.kind === 'rotate' && keys ? <RotateSteps keyRow={target.key} keys={keys} /> : null}
      </WriteDialog>
    </AdminPage>
  );
}
